import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  IconButton,
  Stack,
  MenuItem,
} from '@mui/material';
import { Save as SaveIcon, Add as AddIcon, Delete as DeleteIcon, Image as ImageIcon } from '@mui/icons-material';
import useSWR from 'swr';
import axios from 'axios';
import { apiHost } from '../utils/apiHost';
import { konfirmasi } from '../utils/dialog';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true,
});

const fetcher = (url) => axiosInstance.get(url).then((res) => res.data);

// Konten homepage Web Utama (landing page marketing di frontendKasirLayoutMejaCabang), khusus superadmin.
const WebUtamaCard = ({ data, onSaved }) => {
  const [form, setForm] = useState({ webUtamaHeroBadge: '', webUtamaHeroTitle: '', webUtamaHeroSubtitle: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data) {
      setForm({
        webUtamaHeroBadge: data.webUtamaHeroBadge || '',
        webUtamaHeroTitle: data.webUtamaHeroTitle || '',
        webUtamaHeroSubtitle: data.webUtamaHeroSubtitle || '',
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Homepage Web Utama berhasil disimpan.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Homepage Web Utama"
        subheader="Teks hero di halaman utama (landing page pemasaran Juragan Kuliner). Kosongkan untuk memakai teks bawaan."
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Badge Hero"
              name="webUtamaHeroBadge"
              placeholder="POS untuk Restoran, Cafe & Toko Retail"
              value={form.webUtamaHeroBadge}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Judul Hero"
              name="webUtamaHeroTitle"
              placeholder="Kasir Online untuk Restoran/Cafe & Retail Anda"
              value={form.webUtamaHeroTitle}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Deskripsi Hero"
              name="webUtamaHeroSubtitle"
              placeholder="Kelola transaksi kasir, stok produk, laporan penjualan..."
              value={form.webUtamaHeroSubtitle}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan
            </Button>
          </Grid>
          {feedback && (
            <Grid item xs={12}>
              <Alert severity={feedback.type}>{feedback.msg}</Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

// Konten homepage default Web Menu (aplikasi menu.qasir, tampil saat diakses tanpa subdomain cabang), khusus superadmin.
const WebMenuDefaultCard = ({ data, onSaved }) => {
  const [form, setForm] = useState({ webMenuJudul: '', webMenuDeskripsi: '', webMenuAlamat: '', webMenuTelp: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data) {
      setForm({
        webMenuJudul: data.webMenuJudul || '',
        webMenuDeskripsi: data.webMenuDeskripsi || '',
        webMenuAlamat: data.webMenuAlamat || '',
        webMenuTelp: data.webMenuTelp || '',
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => formData.append(key, value));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Homepage Web Menu berhasil disimpan.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Homepage Web Menu (Default)"
        subheader="Ditampilkan saat Web Menu diakses tanpa subdomain cabang (mis. langsung ke domain utama). Tiap cabang punya halaman menu sendiri yang diatur lewat menu Konfigurasi Nota & PPN / Cabang."
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Judul"
              name="webMenuJudul"
              placeholder="Selamat Datang di Juragan Kuliner"
              value={form.webMenuJudul}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Deskripsi"
              name="webMenuDeskripsi"
              placeholder="Silakan pindai kode QR yang tersedia di meja atau konter untuk melihat menu dan memesan langsung dari ponsel Anda."
              value={form.webMenuDeskripsi}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Alamat (opsional)"
              name="webMenuAlamat"
              value={form.webMenuAlamat}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nomor Telepon (opsional)"
              name="webMenuTelp"
              value={form.webMenuTelp}
              onChange={handleChange}
              disabled={saving}
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan
            </Button>
          </Grid>
          {feedback && (
            <Grid item xs={12}>
              <Alert severity={feedback.type}>{feedback.msg}</Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const defaultPlan = () => ({ name: '', price: '', period: '', featured: false, badge: '', featuresText: '', btnText: 'Pilih Paket' });

// Daftar paket harga di Web Utama. Fitur disimpan sebagai textarea satu baris = satu fitur;
// baris diawali "!" berarti fitur itu ditandai tidak tersedia (dicoret) di UI.
const PricingPlansCard = ({ data, onSaved }) => {
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.webUtamaPricingPlans) {
      setPlans(data.webUtamaPricingPlans.map((p) => ({
        name: p.name || '',
        price: p.price || '',
        period: p.period || '',
        featured: !!p.featured,
        badge: p.badge || '',
        featuresText: (p.features || []).map((f) => (f.na ? `!${f.text}` : f.text)).join('\n'),
        btnText: p.btnText || '',
      })));
    }
  }, [data]);

  const updatePlan = (idx, changes) => {
    setPlans((prev) => prev.map((p, i) => (i === idx ? { ...p, ...changes } : p)));
  };
  const addPlan = () => setPlans((prev) => [...prev, defaultPlan()]);
  const removePlan = (idx) => setPlans((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = plans.map((p) => ({
        name: p.name,
        price: p.price,
        period: p.period,
        featured: p.featured,
        badge: p.badge,
        btnText: p.btnText,
        features: p.featuresText.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => (
          line.startsWith('!') ? { text: line.slice(1).trim(), na: true } : { text: line, na: false }
        )),
      }));
      const formData = new FormData();
      formData.append('webUtamaPricingPlans', JSON.stringify(payload));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Daftar harga berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Harga (Pricing Plans)"
        subheader={'Daftar paket harga di Web Utama. Kosongkan semua paket (hapus semua kartu) untuk menyembunyikan bagian Harga di homepage. Tiap baris di kolom Fitur = satu item; awali baris dengan "!" untuk menandai fitur yang TIDAK tersedia di paket itu.'}
      />
      <CardContent>
        <Stack spacing={2}>
          {plans.map((plan, idx) => (
            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Nama Paket" value={plan.name} onChange={(e) => updatePlan(idx, { name: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Harga" value={plan.price} onChange={(e) => updatePlan(idx, { price: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField fullWidth label="Periode" value={plan.period} onChange={(e) => updatePlan(idx, { period: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                  <IconButton color="error" onClick={() => removePlan(idx)} disabled={saving}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={<Switch checked={plan.featured} onChange={(e) => updatePlan(idx, { featured: e.target.checked })} disabled={saving} />}
                    label="Tandai sebagai Populer"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Label Badge (jika Populer)" placeholder="PALING POPULER" value={plan.badge} onChange={(e) => updatePlan(idx, { badge: e.target.value })} disabled={saving || !plan.featured} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Fitur (satu per baris)"
                    placeholder={'1 outlet / cabang\nHingga 500 produk\n!Multi-cabang'}
                    value={plan.featuresText}
                    onChange={(e) => updatePlan(idx, { featuresText: e.target.value })}
                    disabled={saving}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Teks Tombol" value={plan.btnText} onChange={(e) => updatePlan(idx, { btnText: e.target.value })} disabled={saving} />
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addPlan} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Tambah Paket
          </Button>
          <Divider />
          <Box>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan Harga
            </Button>
          </Box>
          {feedback && <Alert severity={feedback.type}>{feedback.msg}</Alert>}
        </Stack>
      </CardContent>
    </Card>
  );
};

const defaultTestimonial = () => ({ quote: '', name: '', role: '', initials: '' });
const deriveInitials = (name) => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('');

// Daftar testimoni di Web Utama.
const TestimonialsCard = ({ data, onSaved }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.webUtamaTestimonials) {
      setItems(data.webUtamaTestimonials.map((t) => ({
        quote: t.quote || '', name: t.name || '', role: t.role || '', initials: t.initials || '',
      })));
    }
  }, [data]);

  const updateItem = (idx, changes) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...changes } : it)));
  const addItem = () => setItems((prev) => [...prev, defaultTestimonial()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = items.map((it) => ({
        quote: it.quote,
        name: it.name,
        role: it.role,
        initials: it.initials || deriveInitials(it.name || ''),
      }));
      const formData = new FormData();
      formData.append('webUtamaTestimonials', JSON.stringify(payload));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Testimoni berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Testimoni"
        subheader="Daftar testimoni di Web Utama. Kosongkan semua (hapus semua kartu) untuk menyembunyikan bagian Testimoni di homepage. Inisial otomatis diambil dari nama jika dikosongkan."
      />
      <CardContent>
        <Stack spacing={2}>
          {items.map((it, idx) => (
            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} label="Kutipan" value={it.quote} onChange={(e) => updateItem(idx, { quote: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Nama" value={it.name} onChange={(e) => updateItem(idx, { name: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth label="Jabatan / Usaha" placeholder="Owner, Kopi Sudut" value={it.role} onChange={(e) => updateItem(idx, { role: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={8} sm={3}>
                  <TextField fullWidth label="Inisial" placeholder="Otomatis dari nama" value={it.initials} onChange={(e) => updateItem(idx, { initials: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={4} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <IconButton color="error" onClick={() => removeItem(idx)} disabled={saving}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addItem} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Tambah Testimoni
          </Button>
          <Divider />
          <Box>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan Testimoni
            </Button>
          </Box>
          {feedback && <Alert severity={feedback.type}>{feedback.msg}</Alert>}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Satu slot gambar showcase (upload + tag + keterangan), state & simpan independen per slot.
const ShowcaseSlot = ({ slot, label, data, onSaved }) => {
  const imageField = `webUtamaShowcase${slot}Image`;
  const tagField = `webUtamaShowcase${slot}Tag`;
  const captionField = `webUtamaShowcase${slot}Caption`;
  const [tag, setTag] = useState('');
  const [caption, setCaption] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data) {
      setTag(data[tagField] || '');
      setCaption(data[captionField] || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const currentUrl = data?.[imageField] ? `${getApiBaseUrl()}/uploads/${data[imageField]}` : null;

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append(tagField, tag);
      formData.append(captionField, caption);
      if (file) formData.append(`showcase${slot}`, file);
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFile(null);
      setPreview(null);
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Tersimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!await konfirmasi('Hapus gambar ini? Slot ini akan disembunyikan dari homepage.')) return;
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append(`removeShowcase${slot}`, true);
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Gambar dihapus.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menghapus.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid item xs={12} md={4}>
      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, height: '100%' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{label}</Typography>
        <Box sx={{
          width: '100%', height: 140, border: '1px dashed #ccc', borderRadius: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa', mb: 1,
        }}>
          {(preview || currentUrl) ? (
            <img src={preview || currentUrl} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <ImageIcon sx={{ color: '#ccc', fontSize: 36 }} />
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button variant="outlined" component="label" size="small" disabled={saving}>
            Pilih Gambar
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files[0];
                setFile(f || null);
                setPreview(f ? URL.createObjectURL(f) : null);
              }}
            />
          </Button>
          {currentUrl && !file && (
            <Button variant="outlined" color="error" size="small" onClick={handleRemove} disabled={saving}>
              Hapus
            </Button>
          )}
        </Stack>
        <TextField fullWidth size="small" label="Tag" placeholder="Kasir Web · Retail" value={tag} onChange={(e) => setTag(e.target.value)} disabled={saving} sx={{ mb: 1.5 }} />
        <TextField fullWidth size="small" multiline minRows={2} label="Keterangan" placeholder="Scan barcode & kelola transaksi langsung dari browser." value={caption} onChange={(e) => setCaption(e.target.value)} disabled={saving} sx={{ mb: 1.5 }} />
        <Button variant="contained" size="small" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave} fullWidth>
          Simpan
        </Button>
        {feedback && <Alert severity={feedback.type} sx={{ mt: 1 }}>{feedback.msg}</Alert>}
      </Box>
    </Grid>
  );
};

// 3 slot gambar showcase (screenshot aplikasi) di Web Utama, khusus superadmin.
const ShowcaseImagesCard = ({ data, onSaved }) => (
  <Card sx={{ mb: 3 }}>
    <CardHeader
      title="Gambar Showcase (Screenshot Aplikasi)"
      subheader="3 gambar yang tampil di bagian Teknologi Web Utama. Slot 1 tampil dengan bingkai browser, Slot 2 & 3 dengan bingkai HP. Kosongkan (hapus) semua slot untuk menyembunyikan bagian Teknologi ini di homepage."
    />
    <CardContent>
      <Grid container spacing={2}>
        <ShowcaseSlot slot={1} label="Slot 1 — Bingkai Browser (Web)" data={data} onSaved={onSaved} />
        <ShowcaseSlot slot={2} label="Slot 2 — Bingkai HP" data={data} onSaved={onSaved} />
        <ShowcaseSlot slot={3} label="Slot 3 — Bingkai HP" data={data} onSaved={onSaved} />
      </Grid>
    </CardContent>
  </Card>
);

const ICON_COLORS = ['green', 'gold', 'purple', 'blue', 'coral', 'teal'];
const defaultFeature = () => ({ icon: '✨', iconClass: 'green', title: '', description: '', tagsText: '' });

// Daftar kartu fitur di bagian "Fitur Lengkap" Web Utama.
const FeaturesCard = ({ data, onSaved }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.webUtamaFeatures) {
      setItems(data.webUtamaFeatures.map((f) => ({
        icon: f.icon || '✨',
        iconClass: f.iconClass || 'green',
        title: f.title || '',
        description: f.description || '',
        tagsText: (f.tags || []).join(', '),
      })));
    }
  }, [data]);

  const updateItem = (idx, changes) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...changes } : it)));
  const addItem = () => setItems((prev) => [...prev, defaultFeature()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const payload = items.map((it) => ({
        icon: it.icon,
        iconClass: it.iconClass,
        title: it.title,
        description: it.description,
        tags: it.tagsText.split(',').map((t) => t.trim()).filter(Boolean),
      }));
      const formData = new FormData();
      formData.append('webUtamaFeatures', JSON.stringify(payload));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Daftar fitur berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Fitur Lengkap"
        subheader="Kartu-kartu fitur di Web Utama. Kosongkan semua (hapus semua kartu) untuk menyembunyikan bagian ini di homepage."
      />
      <CardContent>
        <Stack spacing={2}>
          {items.map((it, idx) => (
            <Box key={idx} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth label="Ikon (emoji)" value={it.icon} onChange={(e) => updateItem(idx, { icon: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth select label="Warna" value={it.iconClass} onChange={(e) => updateItem(idx, { iconClass: e.target.value })} disabled={saving}>
                    {ICON_COLORS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <TextField fullWidth label="Judul" value={it.title} onChange={(e) => updateItem(idx, { title: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                  <IconButton color="error" onClick={() => removeItem(idx)} disabled={saving}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth multiline minRows={2} label="Deskripsi" value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} disabled={saving} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Tag (pisahkan dengan koma)" placeholder="Multi-cabang, Stok Real-time" value={it.tagsText} onChange={(e) => updateItem(idx, { tagsText: e.target.value })} disabled={saving} />
                </Grid>
              </Grid>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addItem} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Tambah Fitur
          </Button>
          <Divider />
          <Box>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan Fitur
            </Button>
          </Box>
          {feedback && <Alert severity={feedback.type}>{feedback.msg}</Alert>}
        </Stack>
      </CardContent>
    </Card>
  );
};

// Panel split (dipakai untuk "Kasir Transaksi" & "Laporan Cerdas"): label, judul, deskripsi, daftar poin, teks tombol.
const SplitPanelCard = ({ title, field, data, onSaved, placeholders }) => {
  const [form, setForm] = useState({ label: '', heading: '', description: '', bulletsText: '', buttonText: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    const val = data?.[field];
    if (val) {
      setForm({
        label: val.label || '',
        heading: val.heading || '',
        description: val.description || '',
        bulletsText: (val.bullets || []).join('\n'),
        buttonText: val.buttonText || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const isEmpty = !form.heading && !form.description && !form.bulletsText;
      const payload = isEmpty ? null : {
        label: form.label,
        heading: form.heading,
        description: form.description,
        bullets: form.bulletsText.split('\n').map((l) => l.trim()).filter(Boolean),
        buttonText: form.buttonText,
      };
      const formData = new FormData();
      formData.append(field, payload ? JSON.stringify(payload) : '');
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: `${title} berhasil disimpan.` });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={title}
        subheader="Kosongkan Judul, Deskripsi, dan Poin sekaligus untuk menyembunyikan bagian ini di homepage."
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Label Kecil" placeholder={placeholders.label} name="label" value={form.label} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12} sm={8}>
            <TextField fullWidth label="Judul" placeholder={placeholders.heading} name="heading" value={form.heading} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Deskripsi" placeholder={placeholders.description} name="description" value={form.description} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={4} label="Poin (satu per baris)" placeholder={placeholders.bullets} name="bulletsText" value={form.bulletsText} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Teks Tombol" placeholder={placeholders.buttonText} name="buttonText" value={form.buttonText} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan
            </Button>
          </Grid>
          {feedback && (
            <Grid item xs={12}>
              <Alert severity={feedback.type}>{feedback.msg}</Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const defaultStat = () => ({ number: '', desc: '' });

// Daftar angka statistik di "stats band" (mis. "200+ restoran, cafe, dan toko").
const StatsCard = ({ data, onSaved }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.webUtamaStats) {
      setItems(data.webUtamaStats.map((s) => ({ number: s.number || '', desc: s.desc || '' })));
    }
  }, [data]);

  const updateItem = (idx, changes) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...changes } : it)));
  const addItem = () => setItems((prev) => [...prev, defaultStat()]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const formData = new FormData();
      formData.append('webUtamaStats', JSON.stringify(items));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'Statistik berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Statistik (Stats Band)"
        subheader='Angka-angka besar di pita hijau (mis. "200+ Restoran, cafe, dan toko"). Kosongkan semua untuk menyembunyikan bagian ini.'
      />
      <CardContent>
        <Stack spacing={2}>
          {items.map((it, idx) => (
            <Grid container spacing={2} key={idx} alignItems="center">
              <Grid item xs={5} sm={3}>
                <TextField fullWidth label="Angka" placeholder="200+" value={it.number} onChange={(e) => updateItem(idx, { number: e.target.value })} disabled={saving} />
              </Grid>
              <Grid item xs={6} sm={8}>
                <TextField fullWidth label="Keterangan" placeholder="Restoran, cafe, dan toko menggunakan Juragan Kuliner" value={it.desc} onChange={(e) => updateItem(idx, { desc: e.target.value })} disabled={saving} />
              </Grid>
              <Grid item xs={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <IconButton color="error" onClick={() => removeItem(idx)} disabled={saving}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button startIcon={<AddIcon />} onClick={addItem} disabled={saving} sx={{ alignSelf: 'flex-start' }}>
            Tambah Statistik
          </Button>
          <Divider />
          <Box>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan Statistik
            </Button>
          </Box>
          {feedback && <Alert severity={feedback.type}>{feedback.msg}</Alert>}
        </Stack>
      </CardContent>
    </Card>
  );
};

// CTA band paling bawah sebelum footer.
const CtaCard = ({ data, onSaved }) => {
  const [form, setForm] = useState({ heading: '', description: '', primaryText: '', secondaryText: '' });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    if (data?.webUtamaCta) {
      setForm({
        heading: data.webUtamaCta.heading || '',
        description: data.webUtamaCta.description || '',
        primaryText: data.webUtamaCta.primaryText || '',
        secondaryText: data.webUtamaCta.secondaryText || '',
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const isEmpty = !form.heading && !form.description;
      const formData = new FormData();
      formData.append('webUtamaCta', isEmpty ? '' : JSON.stringify(form));
      await axiosInstance.put('/updatepengaturanutama', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved?.();
      setFeedback({ type: 'success', msg: 'CTA berhasil disimpan.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err.response?.data?.message || err.response?.data?.msg || 'Gagal menyimpan.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="CTA Band (Pita Ajakan Terakhir)"
        subheader="Bagian ajakan sebelum footer. Kosongkan Judul dan Deskripsi sekaligus untuk menyembunyikan bagian ini."
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Judul" placeholder="Siap modernisasi Restoran, Cafe, atau Toko Anda?" name="heading" value={form.heading} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} label="Deskripsi" placeholder="Bergabung dengan ratusan bisnis kuliner dan retail..." name="description" value={form.description} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Teks Tombol Utama" placeholder="Mulai Demo" name="primaryText" value={form.primaryText} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Teks Tombol Kedua" placeholder="Jadwalkan Demo" name="secondaryText" value={form.secondaryText} onChange={handleChange} disabled={saving} />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}>
              Simpan
            </Button>
          </Grid>
          {feedback && (
            <Grid item xs={12}>
              <Alert severity={feedback.type}>{feedback.msg}</Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export const HomepageSettings = () => {
  const { data, isLoading, mutate } = useSWR('/getpengaturanutama', fetcher);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Pengaturan Homepage</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Atur teks yang tampil di halaman utama Web Utama dan halaman default Web Menu.
      </Typography>
      {isLoading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          <WebUtamaCard data={data?.data} onSaved={mutate} />
          <FeaturesCard data={data?.data} onSaved={mutate} />
          <SplitPanelCard
            title="Panel: Kasir Transaksi"
            field="webUtamaSplit1"
            data={data?.data}
            onSaved={mutate}
            placeholders={{
              label: 'KASIR TRANSAKSI',
              heading: 'Transaksi selesai dalam hitungan detik',
              description: 'Cari produk, scan barcode, atau pilih dari menu...',
              bullets: 'Scan barcode produk retail secara instan\nKelola meja & pesanan dine-in untuk cafe',
              buttonText: 'Coba Sekarang →',
            }}
          />
          <SplitPanelCard
            title="Panel: Laporan Cerdas"
            field="webUtamaSplit2"
            data={data?.data}
            onSaved={mutate}
            placeholders={{
              label: 'LAPORAN CERDAS',
              heading: 'Data real-time, keputusan lebih tepat',
              description: 'Pantau performa penjualan Restoran/Cafe atau toko Anda...',
              bullets: 'Grafik tren penjualan harian dan bulanan\nRanking produk paling laris',
              buttonText: 'Lihat Demo →',
            }}
          />
          <StatsCard data={data?.data} onSaved={mutate} />
          <PricingPlansCard data={data?.data} onSaved={mutate} />
          <TestimonialsCard data={data?.data} onSaved={mutate} />
          <ShowcaseImagesCard data={data?.data} onSaved={mutate} />
          <CtaCard data={data?.data} onSaved={mutate} />
          <WebMenuDefaultCard data={data?.data} onSaved={mutate} />
        </>
      )}
    </Box>
  );
};

export default HomepageSettings;
